import React, {
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { Box } from '@mui/material';
import { motion, AnimatePresence as MotionPresence } from 'framer-motion';

import { useAssistant } from '@core/assistant/providers/Provider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useHiddenWhileReconnecting } from '@core/dashboard/hooks/useHiddenWhileReconnecting';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';

import { EditorAiDataProvider } from './AiScripts/EditorAiDataContext';
import {
  REQUIRE_SPOT_TRIGGER_LINK,
  useRegionIndex,
} from './AiScripts/useRegionIndex';
import { DriftProvider } from './DriftContext';
import { EditorModeProvider, useEditorMode } from './EditorModeContext';
import { MapEditorProvider, useMapEditor } from './MapEditorContext';
import { useActiveBrandingId } from './useActiveBrandingId';
import { useCanUseMapEditor } from './useCanUseMapEditor';
import { useMapEditorCatalog } from './useMapEditorCatalog';
import { EditorNodeKind } from './editorCategories';
import {
  chainEndsInCap,
  chainStartsInLobby,
  resolveStartElement,
} from './mapEditorCatalog';
import {
  dropLeadingLobby,
  dropTrailingCap,
  readPersistedFinishes,
  readPersistedRooms,
  roomIdsOf,
} from './mapEditorRooms';
import {
  clearPreviewLayoutInUnreal,
  previewLayoutInUnreal,
  setEditorModeInUnreal,
  shouldUnrealEditorModeBeOn,
  spawnLayoutInUnreal,
} from './mapEditorUnreal';
import { persistRealtimeEntity } from './realtimeEntityPersistence';
import SceneTree from './SceneTree/SceneTree';
import Inspector from './Inspector/Inspector';
import { SegmentPickerProvider } from './MapEditor/SegmentPickerHost';
import BrandingBar from './BrandingBar/BrandingBar';
import { useEditorStyles } from './styles';

const AnimatePresence = MotionPresence as FC<{ children: ReactNode }>;

export const UESelectionBridge = () => {
  const {
    selectedCustomizationUeId,
    customizationSelectionRevision,
    currentRoom,
    suppressedCustomizationSelectIdRef,
  } = useMytaverseEvent();
  const assistant = useAssistant();
  const mapName =
    currentRoom?.map?.name ?? assistant?.currentMapName ?? undefined;
  const regionIndex = useRegionIndex(mapName);
  const {
    selectSpot,
    selectRegionScript,
    setActiveFocus,
    activeFocus,
    setAiTreeSection,
    registerSceneClick,
  } = useEditorMode();

  useEffect(() => {
    if (!selectedCustomizationUeId) {
      return;
    }
    // Unreal echoes back a CUSTOMISATION_ITEM_SELECTED for the spot a
    // teleport-to-spot just landed on — Inspector.tsx's teleport handler
    // marks that ueId here right beforehand so this specific re-selection
    // (which would otherwise reopen a pane the handler just closed) is
    // skipped exactly once, instead of every future legitimate selection.
    if (
      suppressedCustomizationSelectIdRef.current === selectedCustomizationUeId
    ) {
      suppressedCustomizationSelectIdRef.current = null;
      return;
    }
    // Always remember the click — the AI Scripts create popover surfaces
    // these as "Recent scene clicks" even after the user switches focus.
    registerSceneClick(selectedCustomizationUeId);
    if (activeFocus === 'assistant') {
      const isLinkedToTrigger =
        !!regionIndex.spotToRegion[selectedCustomizationUeId.toLowerCase()];
      if (!REQUIRE_SPOT_TRIGGER_LINK || isLinkedToTrigger) {
        // In AI focus, a spot click opens the AI inspector for that spot's
        // trigger. Spots live in the unified Scripts section, so make sure
        // it's active if the user was browsing Knowledge.
        setAiTreeSection('scripts');
        selectRegionScript(selectedCustomizationUeId, null);
        return;
      }
      // Not linked to a trigger — no AI script/knowledge config for it, fall
      // back to the plain Spot inspector instead.
      setActiveFocus('spots');
      selectSpot(selectedCustomizationUeId);
      return;
    }
    if (activeFocus !== 'spots') {
      setActiveFocus('spots');
    }
    selectSpot(selectedCustomizationUeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomizationUeId, customizationSelectionRevision, regionIndex]);

  return null;
};

const HotspotPlacementBridge = () => {
  const {
    pendingEditorHotspotSelection,
    setPendingEditorHotspotSelection,
    pendingHotspotPlacement,
    setPendingHotspotPlacement,
  } = useMytaverseEvent();
  const { selectHotspot, setActiveFocus, activeFocus, selectedNode } =
    useEditorMode();
  const wasEditingPlacedHotspotRef = useRef(false);

  useEffect(() => {
    if (!pendingEditorHotspotSelection) {
      return;
    }
    if (activeFocus !== 'hotspots') {
      setActiveFocus('hotspots');
    }
    selectHotspot(pendingEditorHotspotSelection.hotspotId);
    setPendingEditorHotspotSelection(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEditorHotspotSelection]);

  useEffect(() => {
    const placedHotspotId = pendingHotspotPlacement?.isCreated
      ? pendingHotspotPlacement.brandingHotspotId
      : null;
    if (!placedHotspotId) {
      wasEditingPlacedHotspotRef.current = false;
      return;
    }
    const isEditingPlacedHotspot =
      selectedNode?.kind === EditorNodeKind.HOTSPOT &&
      selectedNode.hotspotId === placedHotspotId;
    if (isEditingPlacedHotspot) {
      wasEditingPlacedHotspotRef.current = true;
      return;
    }
    if (wasEditingPlacedHotspotRef.current) {
      wasEditingPlacedHotspotRef.current = false;
      setPendingHotspotPlacement(null);
    }
  }, [selectedNode, pendingHotspotPlacement, setPendingHotspotPlacement]);

  useEffect(
    () => () => {
      setPendingHotspotPlacement(null);
    },
    [setPendingHotspotPlacement],
  );

  return null;
};

// When "follow live trigger" is enabled, walking into an AI trigger
// (SYNC_DATA_UE.audioTrigger.aiTriggerId) auto-opens the inspector for that
// trigger and switches the left panel to AI-trigger mode. When disabled, the
// virtual assistant (Noa) plays normally and the tree only highlights the row.
export const AiTriggerSelectionBridge = () => {
  const { aiTriggerName, currentRoom } = useMytaverseEvent();
  const assistant = useAssistant();
  const mapName =
    currentRoom?.map?.name ?? assistant?.currentMapName ?? undefined;
  const regionIndex = useRegionIndex(mapName);
  const {
    activeFocus,
    followLiveTrigger,
    selectRegionScript,
    setAiTreeSection,
    selectedNode,
    suppressedTriggerSelectRef,
  } = useEditorMode();

  useEffect(() => {
    if (!followLiveTrigger || activeFocus !== 'assistant' || !aiTriggerName) {
      return;
    }
    if (suppressedTriggerSelectRef.current === aiTriggerName) {
      suppressedTriggerSelectRef.current = null;
      return;
    }
    // Defensive: only auto-open for a real region or a spot linked to one —
    // a live trigger name that resolves to neither has no AI script/knowledge
    // surface to show.
    const isLinkedToTrigger =
      regionIndex.regionNames.includes(aiTriggerName) ||
      !!regionIndex.spotToRegion[aiTriggerName.toLowerCase()];
    if (REQUIRE_SPOT_TRIGGER_LINK && !isLinkedToTrigger) {
      return;
    }
    // Skip if the inspector is already showing this trigger — avoids resetting
    // the panel as repeated SYNC_DATA_UE bursts arrive for the same trigger.
    if (
      selectedNode?.kind === EditorNodeKind.REGION_SCRIPT &&
      selectedNode.trigger === aiTriggerName
    ) {
      return;
    }
    setAiTreeSection('scripts');
    selectRegionScript(aiTriggerName, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTriggerName, followLiveTrigger, activeFocus, regionIndex]);

  return null;
};

export const MapEditorModeBridge = () => {
  const { activeFocus, setWalkRequested } = useEditorMode();
  const { sendMessageToUnreal } = useMytaverse();
  const canUseMapEditor = useCanUseMapEditor();
  const shouldBeOn = shouldUnrealEditorModeBeOn(activeFocus, canUseMapEditor);

  const sendRef = useRef(sendMessageToUnreal);
  sendRef.current = sendMessageToUnreal;

  const sentEnabledRef = useRef(false);

  useEffect(() => {
    if (shouldBeOn === sentEnabledRef.current) {
      return;
    }
    sentEnabledRef.current = shouldBeOn;
    // Every send from here names the plan view, so no walk survives one either way. Cleared on the
    // way in as well as on the way out: re-entering the focus re-enters overhead, and a flag left
    // standing from a previous visit would put the walk banner up over a plan view.
    setWalkRequested(false);
    setEditorModeInUnreal(sendRef.current, shouldBeOn);
  }, [shouldBeOn, setWalkRequested]);

  useEffect(
    () => () => {
      if (!sentEnabledRef.current) {
        return;
      }
      sentEnabledRef.current = false;
      setEditorModeInUnreal(sendRef.current, false);
    },
    [],
  );

  return null;
};

/**
 * Keeps the author's local draft previewed in Unreal while the map editor is the active focus.
 *
 * @returns Nothing — this component renders no UI.
 */
export const MapEditorPreviewBridge = () => {
  const { activeFocus } = useEditorMode();
  const { rooms, hasAuthorEdits, finishes, startElement } = useMapEditor();
  const { sendMessageToUnreal } = useMytaverse();
  const canUseMapEditor = useCanUseMapEditor();
  const isActive = activeFocus === 'mapEditor' && canUseMapEditor;

  const sendRef = useRef(sendMessageToUnreal);
  sendRef.current = sendMessageToUnreal;

  const { elements: catalogElements, defaultEndElement } =
    useMapEditorCatalog();
  const catalogRef = useRef(catalogElements);
  catalogRef.current = catalogElements;
  const endElementRef = useRef(defaultEndElement);
  endElementRef.current = defaultEndElement;
  const startElementRef = useRef(startElement);
  startElementRef.current = startElement;

  // The draft flattened to a string so the effect below re-runs on content rather than on identity —
  // every edit produces a new array, and the rooms themselves are what a preview describes. Keys are
  // part of it: reordering two copies of the same module changes nothing about the geometry but does
  // change which placement holds which spots, and the preview has to address them the way Save will.
  //
  // The lobby leads the string rather than getting a dependency of its own, because it is part of
  // the same draft and sits at the same place in it: switching it rebuilds the chain from a
  // different opening, and an author who changed nothing else still has to see that.
  const draftKey = [
    startElement,
    ...rooms.map((room) => `${room.key}:${room.id}`),
  ].join('\0');
  const roomsRef = useRef(rooms);
  roomsRef.current = rooms;
  const hasPreviewedRef = useRef(false);

  // Read through a ref and deliberately not part of draftKey. A finish edit must not re-spawn the
  // geometry — that is what SET_FINISH_PREVIEW exists for, and rebuilding thirty rooms to repaint one
  // wall would stutter the world under the author's cursor. But a spawn that happens for its own
  // reason has to carry them, or a room added to a finished layout comes up in the kit's own
  // materials and stays that way until something else repaints it.
  const finishesRef = useRef(finishes);
  finishesRef.current = finishes;

  useEffect(() => {
    if (!isActive || !hasAuthorEdits) {
      return;
    }
    hasPreviewedRef.current = true;
    previewLayoutInUnreal(
      sendRef.current,
      roomsRef.current,
      catalogRef.current,
      endElementRef.current,
      startElementRef.current,
      finishesRef.current,
    );
  }, [isActive, draftKey, hasAuthorEdits]);

  // A draft that stops being the author's own work has to be taken down, not just stop updating.
  // revertDraft is the case: it puts the standing layout back in the list, and previewing that would
  // build a local copy of rooms the server is already showing — with the published ones hidden
  // behind it, since Unreal hides them for as long as any draft is up. Tearing down instead hands
  // the real rooms back, which is what reverting means.
  //
  // Kept here rather than in whoever calls revertDraft: previews are this bridge's to own, and a
  // second sender would be a second place to keep the draft-visibility flag in step.
  useEffect(() => {
    if (hasAuthorEdits || !hasPreviewedRef.current) {
      return;
    }
    hasPreviewedRef.current = false;
    clearPreviewLayoutInUnreal(sendRef.current);
  }, [hasAuthorEdits]);

  useEffect(
    () => () => {
      if (hasPreviewedRef.current) {
        hasPreviewedRef.current = false;
        clearPreviewLayoutInUnreal(sendRef.current);
      }
    },
    [],
  );

  return null;
};

/**
 * Feeds the authority's geometry report into the map editor's draft state.
 *
 * @returns Nothing — this component renders no UI.
 */
export const MapEditorGeometryBridge = () => {
  const {
    currentGeometryRooms,
    currentGeometryRefusals,
    currentGeometryStartRoom,
    previewRefusals,
  } = useMytaverseEvent();
  const { setBuiltRooms, setRefusals, setPreviewRefusals } = useMapEditor();

  // Only the id, because only the id is a choice. The rest of the standing lobby — its spots and
  // its regions — is read where it is addressed rather than copied into the draft, which holds what
  // the author picked and nothing about what UE built out of it.
  //
  // "Where it is addressed" is SegmentInspector, reading `currentGeometryStartRoom` from this same
  // provider. Naming it is not decoration: for a while nothing addressed it at all, and the entrance
  // panel asked `findStandingSpots(builtRooms, START_ROOM_KEY)` instead — a list this line is what
  // keeps the lobby out of. It answered "not standing yet" for a lobby standing with eight frames,
  // on the same screen as a tree row reading "8 pictures" off the catalog. Both halves were honest
  // and the pair was wrong, which is why the consumer belongs in the comment.
  const standingStartId = currentGeometryStartRoom?.id ?? '';

  // The lobby travels with the rooms rather than on an effect of its own, because the two are halves
  // of one report and the seed they feed is one-shot: split apart, the rooms would claim that seed
  // and leave the lobby out of the very draft they seeded.
  useEffect(() => {
    setBuiltRooms(currentGeometryRooms, standingStartId);
  }, [currentGeometryRooms, standingStartId, setBuiltRooms]);

  // Separate effect from the rooms on purpose: setBuiltRooms also seeds the draft on its first
  // call, and folding the refusals into that would tie a report of what was turned down to a
  // one-shot piece of state it has nothing to do with.
  useEffect(() => {
    setRefusals(currentGeometryRefusals);
  }, [currentGeometryRefusals, setRefusals]);

  // The draft's own refusals, on their own effect for the same reason: they arrive in a different message
  // and describe a different chain, so nothing about them should be tied to a published report landing.
  useEffect(() => {
    setPreviewRefusals(previewRefusals);
  }, [previewRefusals, setPreviewRefusals]);

  return null;
};

/**
 * Loads the open branding's saved real-time entities while the editor is open,
 * and persists a fresh spawn as soon as the engine has reported it.
 *
 * Both calls are admin-scoped, so they live here rather than in the provider,
 * which would fire them for every participant and get a 403. The persist is
 * not in the inspector because the engine rebuilds the room from the saved
 * record on every save in it: an unsaved spawn would vanish at the next one.
 */
export const RealtimeEntitiesBridge = () => {
  const {
    currentRoom,
    currentBrandingId,
    realtimeEntities,
    realtimeEntitiesRevision,
    realtimeEntityUploads,
    realtimeEntitySaving,
    reloadRealtimeEntities,
    upsertRealtimeEntity,
    publishRealtimeEntityProperties,
    trackRealtimeEntitySave,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { setAutosaveStatus } = useEditorMode();

  const roomId = currentRoom?.roomId;
  const brandingId = currentBrandingId ?? currentRoom?.activeBrandingId ?? null;

  useEffect(() => {
    if (!roomId) {
      return;
    }

    void reloadRealtimeEntities(brandingId);
  }, [roomId, brandingId, realtimeEntitiesRevision, reloadRealtimeEntities]);

  // One attempt per entity: a failed save leaves the row dirty with Save as
  // the retry. A blocked attempt is not recorded and re-runs once it clears.
  const persistedRef = useRef(new Set<string>());
  useEffect(() => {
    if (!roomId || !brandingId) {
      return;
    }

    realtimeEntities.forEach((entity) => {
      // Only into the configuration it was spawned under; elsewhere the row
      // would be a phantom.
      if (
        !entity.engineReported ||
        entity.saved ||
        entity.brandingId !== brandingId ||
        persistedRef.current.has(entity.entityId) ||
        (realtimeEntityUploads[entity.entityId] ?? 0) > 0 ||
        realtimeEntitySaving[entity.entityId]
      ) {
        return;
      }

      persistedRef.current.add(entity.entityId);
      setAutosaveStatus('saving');

      const save = persistRealtimeEntity({
        roomId,
        brandingId,
        entity,
        sendMessageToUnreal,
        upsertRealtimeEntity,
        publishProperties: publishRealtimeEntityProperties,
      });
      trackRealtimeEntitySave(entity.entityId, save);

      save.then(
        () => setAutosaveStatus('saved'),
        () => {
          console.error(
            'Failed to persist a spawned entity after the engine reported it',
            {
              roomId,
              brandingId,
              entityId: entity.entityId,
            },
          );
          setAutosaveStatus('error');
        },
      );
    });
  }, [
    brandingId,
    publishRealtimeEntityProperties,
    realtimeEntities,
    realtimeEntitySaving,
    realtimeEntityUploads,
    roomId,
    sendMessageToUnreal,
    setAutosaveStatus,
    trackRealtimeEntitySave,
    upsertRealtimeEntity,
  ]);

  return null;
};

/**
 * Hydrates the draft from the active configuration's saved layout and republishes it to Unreal.
 *
 * Republished on arrival, on a configuration switch, when the room kit finally resolves, and every time
 * Unreal's socket comes up — the last of those being what puts the world back after a UE session is
 * replaced under an unchanged configuration. See the dependency list for why each one is a trigger.
 *
 * @returns Nothing — this component renders no UI.
 */
export const MapEditorPersistenceBridge = () => {
  const currentBrandingId = useActiveBrandingId();
  const { ue5WebsocketConnected } = useMytaverseEvent();
  const { hydrateFromPersisted } = useMapEditor();
  const { sendMessageToUnreal } = useMytaverse();
  const { elements: catalogElements, defaultEndElement } =
    useMapEditorCatalog();

  const canSpawnLayout = useCanUseMapEditor();

  const sendRef = useRef(sendMessageToUnreal);
  sendRef.current = sendMessageToUnreal;
  const catalogRef = useRef(catalogElements);
  catalogRef.current = catalogElements;
  const endElementRef = useRef(defaultEndElement);
  endElementRef.current = defaultEndElement;
  const canSpawnRef = useRef(canSpawnLayout);
  canSpawnRef.current = canSpawnLayout;

  // The configuration the previous run of this effect was for. Entering a room and activating another
  // configuration take this same code path but are not the same question, and only this tells them
  // apart — see the empty-payload rule below, which is right for one and wrong for the other.
  const readScopeRef = useRef<string | null>(null);

  // How many times Unreal's socket has come up. Bumped on the rising edge only, and it is what makes a
  // replaced UE session republish.
  //
  // The republish used to hang off `currentBrandingId` alone, which is a fact about the configuration
  // rather than about the world: a UE restarted on the same seat comes up with an empty map while that
  // id never changes, so nothing re-sent the layout and the participant stood in a world with no rooms
  // for the rest of the session. The branding read is repeated with it rather than cached, because the
  // record may well have been edited by somebody else while this client was disconnected.
  //
  // A rising edge rather than the flag itself, for two separate reasons. `ue5WebsocketConnected` also
  // goes *down*, and re-running the read on a disconnect buys nothing but a request. And a counter is
  // what lets a second connection re-trigger an effect whose other inputs are unchanged — a boolean
  // that ends up back at the value it already had is not a dependency change at all.
  const [ueSessionEpoch, setUeSessionEpoch] = useState(0);
  const ueWasConnectedRef = useRef(ue5WebsocketConnected);

  useEffect(() => {
    if (ue5WebsocketConnected && !ueWasConnectedRef.current) {
      setUeSessionEpoch((epoch) => epoch + 1);
    }
    ueWasConnectedRef.current = ue5WebsocketConnected;
  }, [ue5WebsocketConnected]);

  useEffect(() => {
    if (!currentBrandingId) {
      return undefined;
    }

    // Claimed here, synchronously as the read starts, rather than when its response lands. A run
    // whose response never lands is exactly the case this has to survive: an author who activates
    // another configuration before the first read resolves cancels that read, and a scope recorded
    // only on the response would leave `readScopeRef` empty — so the switch that follows would read
    // as an arrival and take the entry branch of the rule below, which is the bug that rule exists
    // to fix. What the author was on is decided by which configuration was asked for, not by which
    // answer came back.
    const previousScope = readScopeRef.current;
    readScopeRef.current = currentBrandingId;

    const isSwitch = !!previousScope && previousScope !== currentBrandingId;

    // Guards against a branding switch resolving out of order: a slow response for the
    // branding the author just left must not overwrite the one they are now on.
    let cancelled = false;

    BrandingsService.getBranding(currentBrandingId)
      .then((branding) => {
        if (cancelled) {
          return;
        }
        // Read once, used twice on purpose: the draft and the republish below have to describe the
        // same placements, or the author edits spots the world does not have.
        //
        // The read is deterministic, which is what makes the republish safe to do on every entry: a
        // v1 record takes the keys UE already stamped rather than fresh ones (see legacyRoomKey), so
        // every participant sends the chain that is already standing and UE answers by doing nothing.
        //
        // A cap the record carries as one of its own rooms is dropped here, where both the saved
        // layout and the catalog are in hand — the report parser cannot tell that row from the cap
        // this hub appends at publish time, so keeping it would leave the draft permanently longer
        // than what is reported standing. See dropTrailingCap.
        const saved = readPersistedRooms(branding?.layout?.rooms);

        // Read against the rooms that survived, so an override addressed to a placement the read
        // dropped or re-keyed goes with it rather than travelling on as dead weight.
        const savedFinishes = readPersistedFinishes(branding?.layout, saved);

        // Whether the record carried a cap at all, asked before the drop and independently of it.
        // Either way that row is this hub's to reconcile rather than the author's edit: dropped, we
        // rewrote their list ourselves; kept — which happens when the kit no longer names that
        // element as its `defaultEndElement` — it can never match the geometry report, because the
        // report drops every cap. Left to the normal rule, the first case shows a spurious "unsaved"
        // banner until a report lands and the second shows it on every entry for good. So the
        // hydrated rooms are recorded as published: the baseline is what this hub is about to send.
        const carriedSavedCap = chainEndsInCap(
          roomIdsOf(saved),
          catalogRef.current,
        );

        const cappedRooms = dropTrailingCap(saved, endElementRef.current);

        // The mirror of the cap drop, at the head of the chain. A record can carry the lobby as one
        // of its own rooms — the flat catalog list that preceded the picker offered every element —
        // and left in place that row can never match the report, which drops every anchor. See
        // dropLeadingLobby.
        const carriedLobby = chainStartsInLobby(
          roomIdsOf(cappedRooms),
          catalogRef.current,
        )
          ? cappedRooms[0].id
          : '';

        const rooms = dropLeadingLobby(cappedRooms, catalogRef.current);

        // The author's chosen lobby, taken off the record rather than defaulted in from the kit.
        // `defaultStartElement` is deliberately not consulted here: it says which lobby this kit
        // considers its default, not which one this layout opens in, and sending it for a record
        // that names none would hide the entrance the map bakes in behind an identical streamed
        // copy — on every configuration that predates the feature, at the cost of a world rebuild
        // each. See prependStartRoom.
        //
        // A lobby the record carried as a room is adopted when the record names none of its own:
        // that row *is* that record's answer to which entrance it opens in, and dropping it without
        // reading it would quietly move the world onto the map's baked entrance instead.
        const rawSavedStart =
          typeof branding?.layout?.startElement === 'string'
            ? branding.layout.startElement.trim()
            : '';

        // Resolved rather than taken as read, and resolved *before* the guard below rather than
        // only inside the payload builder — the two have to agree about whether there is a lobby to
        // send. An id this build cannot describe reaches the wire as nothing, so counting it as
        // something to publish is how the guard's own rule gets broken (below), and leaving it
        // standing in the draft is a choice that is never sent and so never matches what comes
        // back — "unsaved" on every entry with nothing on screen that differs.
        //
        // The cost is that a Save afterwards drops the dead id from the record. That is the right
        // direction here, unlike for the cap: an unresolvable cap leaves a wall the author can see
        // and explain, while an unresolvable lobby is invisible, so keeping it buys nothing and
        // costs a permanent false "unsaved".
        //
        // Only once there is a catalog to resolve against. With none, `canSpawnLayout` is false and
        // this hub publishes nothing at all, so the raw id is carried through untouched rather than
        // dropped from the record it is the only copy of.
        const savedStart = catalogRef.current.length
          ? resolveStartElement(
              rawSavedStart || carriedLobby,
              catalogRef.current,
            )
          : rawSavedStart;

        hydrateFromPersisted(
          rooms,
          currentBrandingId,
          // A carried lobby row counts the same way a carried cap does: either way this hub
          // rewrote the author's list itself, so the hydrated rooms are the baseline rather than
          // an unsaved edit.
          carriedSavedCap || !!carriedLobby,
          savedFinishes,
          savedStart,
        );

        if (!canSpawnRef.current) {
          return;
        }

        // Note what is deliberately NOT tested here: whether Unreal's socket is up. `ue5WebsocketConnected`
        // is a trigger and never a precondition, because it is not a reliable statement about UE's
        // presence — it is set from the UE5_WS_CONNECTED announcement, which is made once per UE session
        // to whoever is attached at that instant. A page that reloads into a session already in the venue
        // never hears it and the flag stays false for good (the same shape as CLIENT_READY; see
        // MytaStreamsProvider's rebind path), so gating the publish on it would silence exactly the
        // reconnect this bridge has to serve. An early publish that finds no UE connection is dropped by
        // the backend fan-out and costs nothing; the epoch above covers the case where UE arrives later.

        // An empty layout is published too, and it is the case this republish matters most for.
        // The map's entrance module stands on the level whether or not anything was ever saved,
        // so a configuration with no rooms is not an empty world — it is that entrance with its
        // exit hanging open, and whoever enters first walks out of it and falls. What goes out is
        // the cap on its own, which seals that doorway (see appendEndCap).
        //
        // With no cap resolved there is nothing to seal it with and the payload is a bare
        // clearExisting. Sending that **on entry** would tear down whatever is standing, so the
        // first read still declines to. A switch is the opposite case: the author moved to a
        // configuration that describes no rooms, and "no rooms" is its answer — declining to send
        // it leaves the rooms of the configuration they left standing in a world that no longer
        // claims them, and nothing corrects it until their first edit previews the new draft over
        // the top. That is the state this reads as "the map never changed".
        //
        // A kit whose cap ships no geometry is the normal case rather than an edge one: the hub
        // resolves no cap for it on purpose (see resolveDefaultEndElement), so on those maps every
        // switch to a fresh configuration lands here.
        //
        // A saved lobby counts as something to publish for the same reason the cap does: the
        // payload is no longer a bare clearExisting, it is a layout whose only room is the
        // entrance the author chose. Left out of this test, a configuration holding a lobby and no
        // modules would never publish its entrance on arrival.
        //
        // `savedStart` is resolved above, which is what makes it safe to test here: an id the
        // catalog cannot describe is dropped by prependStartRoom, so counting the raw value would
        // let a record naming a lobby this build no longer ships fall through to a payload with no
        // rooms at all — the bare clearExisting this branch exists to withhold.
        if (
          !isSwitch &&
          !rooms.length &&
          !endElementRef.current &&
          !savedStart
        ) {
          return;
        }
        spawnLayoutInUnreal(
          sendRef.current,
          rooms,
          catalogRef.current,
          endElementRef.current,
          savedStart,
          savedFinishes,
        );
      })
      .catch((error: Error) => {
        console.error(
          `Failed to load the saved map layout: ${error?.message}`,
          error,
        );
      });

    return () => {
      cancelled = true;
    };
    // Three triggers, and only the first is about the configuration. `canSpawnLayout` is here because
    // the kit arrives on its own schedule — it is read off the streamed build's record, not off the room
    // — and the `canSpawnRef` guard above returns without publishing until it does; with only the
    // branding id to run on, a kit that landed a tick late stranded the layout for the whole session.
    // `ueSessionEpoch` is here because a replaced UE session is a new empty world under an unchanged
    // branding id.
    //
    // The other inputs stay on refs deliberately: the catalog and the cap are read inside an async
    // continuation, where the current value is the one that matters, and putting the array itself in
    // here would republish on every render that happened to rebuild it.
  }, [currentBrandingId, canSpawnLayout, ueSessionEpoch, hydrateFromPersisted]);

  return null;
};

const EditorModeContent = () => {
  const styles = useEditorStyles();
  const { setBrandingActionRequest } = useMytaverseEvent();

  return (
    <Box sx={styles.root}>
      <UESelectionBridge />
      <HotspotPlacementBridge />
      <AiTriggerSelectionBridge />
      <MapEditorModeBridge />
      <MapEditorPreviewBridge />
      <MapEditorGeometryBridge />
      <RealtimeEntitiesBridge />
      {/* Wraps both panels, because both open the picker: the walk in the tree adds and changes
          segments, and the segment panel changes the one it is about. One host means one place where
          a pick becomes an edit — see SegmentPickerHost. */}
      <SegmentPickerProvider>
        <SceneTree />
        <Inspector />
      </SegmentPickerProvider>
      <Box sx={styles.brandingBarWrap}>
        <BrandingBar
          onCreateNew={() => setBrandingActionRequest('create')}
          onOpenLibrary={() => setBrandingActionRequest('library')}
        />
      </Box>
    </Box>
  );
};

const EditorMode = () => {
  const { isBrandingModeEnabled } = useMytaverseEvent();
  // Hidden, never unmounted: a reseat must not cost the author their selection, their open inspector
  // or an unsaved layout draft, and all of that is state inside this subtree. `visibility` rather
  // than `opacity`, because the panels set `pointerEvents: 'auto'` of their own.
  const hiddenWhileReconnecting = useHiddenWhileReconnecting();

  return (
    <MapEditorProvider>
      <MapEditorPersistenceBridge />
      <AnimatePresence>
        {isBrandingModeEnabled ? (
          <motion.div
            key="editor-mode"
            data-testid="editor-mode-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'fixed',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 1200,
              visibility: hiddenWhileReconnecting ? 'hidden' : 'visible',
            }}
          >
            <EditorModeProvider>
              <EditorAiDataProvider>
                <DriftProvider>
                  <EditorModeContent />
                </DriftProvider>
              </EditorAiDataProvider>
            </EditorModeProvider>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </MapEditorProvider>
  );
};

export default EditorMode;
